import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

import ProgressBar from './ProgressBar';
import {
    Leaf,
    Users,
    Wifi,
    Coffee,
    Wind,
    Droplets,
    Sparkles,
    ChevronLeft,
    ChevronRight,
    Calendar,
    Search as SearchIcon,
    Bed,
    Maximize,
    Star
} from 'lucide-react';

// Import room images
import seaViewImg from '../assets/Mahi/37.jpeg';
import heroBg from '../assets/Mahi/44.jpeg';

// Function to determine if a date falls in summer season
const isSummerSeason = (date) => {
    const month = date.getMonth(); // 0-11
    // Summer: April (3) to September (8)
    // Winter: October (9) to March (2)
    return month >= 3 && month <= 8;
};

// Function to get season rates for a date range
const getSeasonRate = (checkInDate) => {
    const isSummer = isSummerSeason(checkInDate);
    return {
        season: isSummer ? 'Summer' : 'Winter',
        singlePrice: isSummer ? 105 : 145,
        doublePrice: isSummer ? 200 : 210
    };
};

// Single room type: Deluxe Room with Sea View
const baseRoom = {
    id: 1,
    name: "Deluxe Room with Sea View",
    category: "Deluxe Room with Sea View",
    description: "Wake up to stunning ocean views and the soothing sounds of waves. Our elegantly designed Deluxe Room with Sea View offers the perfect sanctuary for your Ayurvedic wellness journey.",
    image: seaViewImg,
    size: "35-38 m²",
    occupancy: 2,
    amenities: ["King Size Bed", "Private Balcony", "Rain Shower", "Sea View"],
    features: ["Ocean View", "Private Balcony", "Ayurvedic Amenities"],
    services: [
        { icon: 'Bed', text: 'King Size Bed' },
        { icon: 'Maximize', text: 'Private Sea View Balcony' },
        { icon: 'Droplets', text: 'Rain Shower' },
        { icon: 'Wind', text: 'Air Conditioning' },
        { icon: 'Wifi', text: 'High Speed WiFi' },
        { icon: 'Coffee', text: 'Herbal Tea Station' }
    ]
};

// Icon mapping component
const ServiceIcon = ({ iconName }) => {
    const iconMap = {
        'Bed': Bed,
        'Maximize': Maximize,
        'Droplets': Droplets,
        'Wind': Wind,
        'Wifi': Wifi,
        'Coffee': Coffee,
        'Sparkles': Sparkles
    };

    const IconComponent = iconMap[iconName] || Sparkles;
    return <IconComponent className="w-4 h-4" />;
};

function Search() {
    const location = useLocation();

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Get today's date and 3 days from now (minimum stay) in YYYY-MM-DD format
    const today = new Date();
    const minimumCheckout = new Date();
    minimumCheckout.setDate(today.getDate() + 3);

    const [checkInDate, setCheckInDate] = useState(formatDate(today));
    const [checkOutDate, setCheckOutDate] = useState(formatDate(minimumCheckout));
    const [currentPage, setCurrentPage] = useState(1);
    const [guests, setGuests] = useState(1);
    const [displayedRooms, setDisplayedRooms] = useState([]);
    const [seasonInfo, setSeasonInfo] = useState({ season: '', singlePrice: 0, doublePrice: 0 });

    const roomsPerPage = 6;

    const filterRooms = useCallback(() => {
        const startDate = new Date(checkInDate);
        const endDate = new Date(checkOutDate);
        const numberOfDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

        // Enforce minimum 3 nights stay
        if (numberOfDays < 3) {
            setDisplayedRooms([]);
            return;
        }

        // Get seasonal rates based on check-in date
        const rates = getSeasonRate(startDate);
        setSeasonInfo(rates);

        // Calculate price based on number of guests
        const pricePerNight = guests === 1 ? rates.singlePrice : rates.doublePrice;
        const room = {
            ...baseRoom,
            price: pricePerNight,
            totalPrice: pricePerNight * numberOfDays,
            checkIn: startDate,
            checkOut: endDate,
            nights: numberOfDays,
            season: rates.season
        };

        setDisplayedRooms([room]);
    }, [checkInDate, checkOutDate, guests]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const queryCheckIn = params.get('checkIn');
        const queryCheckOut = params.get('checkOut');
        const isValidDate = (value) => {
            if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
            return !Number.isNaN(new Date(value).getTime());
        };

        const todayForEffect = new Date();
        const effectiveCheckIn = isValidDate(queryCheckIn) ? new Date(queryCheckIn) : todayForEffect;
        const minCheckOut = new Date(effectiveCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        setCheckInDate(formatDate(effectiveCheckIn));

        if (isValidDate(queryCheckOut)) {
            const queryCheckOutDate = new Date(queryCheckOut);
            if (queryCheckOutDate >= minCheckOut) {
                setCheckOutDate(formatDate(queryCheckOutDate));
                return;
            }
        }

        setCheckOutDate(formatDate(minCheckOut));
    }, [location.search]);

    useEffect(() => {
        filterRooms();
    }, [filterRooms]);

    const handleGuestsChange = (e) => {
        setGuests(parseInt(e.target.value, 10));
    };

    const handleCheckInChange = (e) => {
        const newCheckIn = new Date(e.target.value);
        const currentCheckOut = new Date(checkOutDate);
        const minCheckOut = new Date(newCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        setCheckInDate(e.target.value);

        // Ensure checkout is at least 3 nights after check-in
        if (currentCheckOut < minCheckOut) {
            setCheckOutDate(formatDate(minCheckOut));
        }
    };

    const handleCheckOutChange = (e) => {
        const newCheckOut = new Date(e.target.value);
        const currentCheckIn = new Date(checkInDate);
        const minCheckOut = new Date(currentCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        // Ensure minimum 3 nights stay
        if (newCheckOut >= minCheckOut) {
            setCheckOutDate(e.target.value);
        }
    };

    const handleSearch = () => {
        filterRooms();
        setCurrentPage(1);
    };

    const paginatedRooms = displayedRooms.slice(
        (currentPage - 1) * roomsPerPage,
        currentPage * roomsPerPage
    );

    const totalPages = Math.ceil(displayedRooms.length / roomsPerPage);

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50">

            {/* Hero Section */}
            <div className="relative h-[40vh] md:h-[50vh] lg:h-[60vh] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${heroBg})` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-800/70 to-transparent" />
                </div>

                <div className="relative h-full flex items-center">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                        <div className="max-w-2xl">
                            <div className="flex items-center space-x-2 mb-4">
                                <Leaf className="w-6 h-6 md:w-8 md:h-8 text-amber-400" />
                                <span className="text-amber-300 tracking-widest text-xs md:text-sm font-light">
                                    MAHI BEACH AYURVEDA RESORT
                                </span>
                            </div>
                            <h1 className="text-3xl md:text-5xl lg:text-6xl font-light text-white mb-4 leading-tight">
                                Deluxe Room with<br />Sea View
                            </h1>
                            <p className="text-base md:text-lg lg:text-xl text-green-50 font-light leading-relaxed">
                                Experience tranquility and ocean views in our exclusive Deluxe Room with seasonal rates.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <ProgressBar currentStep="search" />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    {/* Filter Sidebar */}
                    <div className="w-full lg:w-80 xl:w-96">
                        <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4">
                            <h2 className="text-2xl font-light text-gray-900 mb-6 flex items-center">
                                <SearchIcon className="w-6 h-6 mr-2 text-green-700" />
                                Book Your Stay
                            </h2>

                            {/* Check In Date */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Check In
                                </label>
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={checkInDate}
                                        min={formatDate(new Date())}
                                        onChange={handleCheckInChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                    />
                                    <Calendar className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                            </div>

                            {/* Check Out Date */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Check Out
                                </label>
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={checkOutDate}
                                        min={(() => {
                                            const minDate = new Date(checkInDate);
                                            minDate.setDate(minDate.getDate() + 3);
                                            return formatDate(minDate);
                                        })()}
                                        onChange={handleCheckOutChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                    />
                                    <Calendar className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Minimum 3 nights from check-in</p>
                            </div>

                            {/* Guests */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Number of Guests
                                </label>
                                <div className="relative">
                                    <select
                                        value={guests}
                                        onChange={handleGuestsChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors appearance-none bg-white"
                                    >
                                        <option value="1">1 Guest (Single Occupancy)</option>
                                        <option value="2">2 Guests (Double Occupancy)</option>
                                    </select>
                                    <Users className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Maximum 2 guests per room</p>
                            </div>

                            {/* Season & Rate Information */}
                            {seasonInfo.season && (
                                <div className="mb-6 p-3 sm:p-4 bg-gradient-to-r from-amber-50 to-green-50 rounded-lg border border-amber-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-medium text-gray-700">Current Season:</span>
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${seasonInfo.season === 'Summer' ? 'bg-amber-200 text-amber-800' : 'bg-blue-200 text-blue-800'
                                            }`}>
                                            {seasonInfo.season}
                                        </span>
                                    </div>

                                    {/* Summer Rates */}
                                    <div className={`mb-3 p-3 rounded-lg border ${seasonInfo.season === 'Summer' ? 'bg-amber-100/50 border-amber-300' : 'bg-white/50 border-gray-200'
                                        }`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-semibold text-amber-700">🌞 Summer Season</span>
                                            {seasonInfo.season === 'Summer' && (
                                                <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Active</span>
                                            )}
                                        </div>
                                        <div className="space-y-1 text-xs sm:text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Single:</span>
                                                <span className={`font-semibold ${seasonInfo.season === 'Summer' ? 'text-green-700' : 'text-gray-600'
                                                    }`}>€105/night</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Double:</span>
                                                <span className={`font-semibold ${seasonInfo.season === 'Summer' ? 'text-green-700' : 'text-gray-600'
                                                    }`}>€200/night</span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">April - September</p>
                                    </div>

                                    {/* Winter Rates */}
                                    <div className={`p-3 rounded-lg border ${seasonInfo.season === 'Winter' ? 'bg-blue-100/50 border-blue-300' : 'bg-white/50 border-gray-200'
                                        }`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-semibold text-blue-700">❄️ Winter Season</span>
                                            {seasonInfo.season === 'Winter' && (
                                                <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Active</span>
                                            )}
                                        </div>
                                        <div className="space-y-1 text-xs sm:text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Single:</span>
                                                <span className={`font-semibold ${seasonInfo.season === 'Winter' ? 'text-green-700' : 'text-gray-600'
                                                    }`}>€145/night</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Double:</span>
                                                <span className={`font-semibold ${seasonInfo.season === 'Winter' ? 'text-green-700' : 'text-gray-600'
                                                    }`}>€210/night</span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">October - March</p>
                                    </div>
                                </div>
                            )}

                            {/* Search Button */}
                            <button
                                onClick={handleSearch}
                                className="w-full bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                            >
                                <SearchIcon className="w-5 h-5" />
                                <span>Update Booking</span>
                            </button>

                            {/* Minimum Stay Notice */}
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs text-blue-800 text-center">
                                    ℹ️ Minimum stay: <span className="font-semibold">3 nights</span>
                                </p>
                            </div>

                            {/* Room Info */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-sm text-gray-600 text-center">
                                    <span className="font-semibold text-green-700">Deluxe Room with Sea View</span>
                                </p>
                                <p className="text-xs text-gray-500 text-center mt-1">
                                    Exclusive accommodation with ocean views
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Room List */}
                    <div className="flex-1">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                            <h2 className="text-2xl md:text-3xl font-light text-gray-900">
                                Deluxe Room with Sea View
                            </h2>
                            {seasonInfo.season && (
                                <div className={`px-4 py-2 rounded-full text-sm font-semibold ${seasonInfo.season === 'Summer' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                    {seasonInfo.season} Season Rates
                                </div>
                            )}
                        </div>

                        {/* Room Cards */}
                        {paginatedRooms.length === 0 ? (
                            <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
                                <Leaf className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-xl text-gray-500">Please select your dates</p>
                                <p className="text-gray-400 mt-2">Choose your check-in and check-out dates</p>
                                <p className="text-sm text-blue-600 mt-4 font-medium">✓ Minimum stay requirement: 3 nights</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {paginatedRooms.map((room) => (
                                    <motion.div
                                        key={room.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
                                    >
                                        <div className="flex flex-col md:flex-row">
                                            {/* Room Image */}
                                            <div className="md:w-2/5 lg:w-1/3 h-64 md:h-auto">
                                                <img
                                                    src={room.image}
                                                    alt={room.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Room Details */}
                                            <div className="flex-1 p-6 lg:p-8">
                                                <div className="flex flex-col h-full">
                                                    {/* Header */}
                                                    <div className="flex-1">
                                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-2">
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                                                        {room.category}
                                                                    </span>
                                                                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${room.season === 'Summer' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                                                                        }`}>
                                                                        {room.season} Rate
                                                                    </span>
                                                                </div>
                                                                <h3 className="text-2xl font-light text-gray-900">
                                                                    {room.name}
                                                                </h3>
                                                            </div>
                                                            <div className="text-right bg-green-50 px-4 py-2 rounded-lg">
                                                                <div className="text-2xl font-bold text-green-700">
                                                                    €{room.price}
                                                                </div>
                                                                <div className="text-xs text-gray-600">{guests === 1 ? 'Single' : 'Double'}</div>
                                                                <div className="text-xs text-gray-500">per night</div>
                                                            </div>
                                                        </div>

                                                        <p className="text-gray-600 mb-4 leading-relaxed">
                                                            {room.description}
                                                        </p>

                                                        {/* Room Info */}
                                                        <div className="flex flex-wrap gap-4 mb-4">
                                                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                                                                <Maximize className="w-4 h-4 text-green-600" />
                                                                <span>{room.size}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                                                                <Users className="w-4 h-4 text-green-600" />
                                                                <span>Up to {room.occupancy} guests</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                                                                <Star className="w-4 h-4 text-amber-500" />
                                                                <span>{room.nights} nights</span>
                                                            </div>
                                                        </div>

                                                        {/* Amenities Icons */}
                                                        <div className="flex flex-wrap gap-3 mb-4">
                                                            {room.services.slice(0, 6).map((service, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="group relative flex items-center justify-center w-10 h-10 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
                                                                >
                                                                    <ServiceIcon iconName={service.icon} />
                                                                    <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                                                        {service.text}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* Total Price */}
                                                        <div className="bg-gradient-to-r from-amber-50 to-green-50 rounded-lg p-4 mb-4 border border-amber-200">
                                                            <div className="flex justify-between items-center">
                                                                <div>
                                                                    <span className="text-sm text-gray-600 block">
                                                                        Total for {room.nights} night{room.nights > 1 ? 's' : ''}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500">
                                                                        {guests === 1 ? 'Single' : 'Double'} occupancy • {room.season} Season
                                                                    </span>
                                                                </div>
                                                                <span className="text-2xl font-bold text-green-700">
                                                                    €{room.totalPrice?.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Buttons */}
                                                    <div className="flex flex-col sm:flex-row gap-3">
                                                        <Link
                                                            to="/rooms/deluxe"
                                                            className="flex-1 text-center border-2 border-green-700 text-green-700 px-6 py-3 rounded-lg font-medium hover:bg-green-50 transition-all"
                                                        >
                                                            View Details
                                                        </Link>
                                                        <Link
                                                            to="/booking"
                                                            state={{
                                                                room: {
                                                                    id: room.id,
                                                                    name: room.name,
                                                                    price: room.price,
                                                                    totalPrice: room.totalPrice,
                                                                    image: room.image,
                                                                    checkIn: room.checkIn,
                                                                    checkOut: room.checkOut,
                                                                    nights: room.nights,
                                                                    services: room.services,
                                                                    season: room.season
                                                                },
                                                                guests: guests
                                                            }}
                                                            className="flex-1 text-center bg-green-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-800 transition-all shadow-md hover:shadow-lg"
                                                        >
                                                            Book Now
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center space-x-2 mt-8">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                {[...Array(totalPages)].map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentPage(index + 1)}
                                        className={`px-4 py-2 rounded-lg font-medium transition-all ${currentPage === index + 1
                                            ? 'bg-green-700 text-white shadow-md'
                                            : 'border border-gray-300 hover:bg-green-50 text-gray-700'
                                            }`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="relative py-16 md:py-24 overflow-hidden mt-12">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.pexels.com/photos/3771640/pexels-photo-3771640.jpeg?auto=compress&cs=tinysrgb&w=1600')",
                    }}
                >
                    <div className="absolute inset-0 bg-green-900/85"></div>
                </div>

                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Leaf className="w-10 h-10 md:w-12 md:h-12 text-amber-400 mx-auto mb-6" />
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
                        Need Help Choosing?
                    </h2>
                    <p className="text-lg md:text-xl text-green-100 mb-8 md:mb-10 font-light leading-relaxed max-w-2xl mx-auto">
                        Our wellness advisors are here to help you find the perfect accommodation for your healing journey.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <Link
                            to="/contact-us"
                            className="w-full sm:w-auto bg-white text-green-800 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-amber-50 transition-all duration-300 shadow-xl hover:shadow-2xl text-center"
                        >
                            Contact Us
                        </Link>
                        <Link
                            to="/rooms/deluxe"
                            className="w-full sm:w-auto border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300 text-center"
                        >
                            View All Amenities
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Search;
